import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export default function ExamActionsMenu({ css, actions = [], categories = [], releasedAt }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = event => {
    event.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 360;
      const opensUp = rect.bottom + menuHeight > window.innerHeight && rect.top > menuHeight;
      const menuWidth = Math.min(236, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
      setMenuPosition(opensUp
        ? { bottom: window.innerHeight - rect.top + 4, left }
        : { top: Math.min(rect.bottom + 4, window.innerHeight - 8), left });
    }
    setOpen(value => !value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = event => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)
      ) setOpen(false);
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const runAction = action => {
    setOpen(false);
    action.onClick();
  };

  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      className={css.examActionMenu}
      style={menuPosition}
      role="menu"
      aria-label="Exam actions"
    >
      {actions.map(action => (
        <button
          key={action.key}
          type="button"
          className={`${css.examActionMenuItem} ${action.danger ? css.examActionMenuItemDanger : ''} ${action.accent ? css.examActionMenuItemAccent : ''}`}
          onClick={() => runAction(action)}
          disabled={action.disabled}
          role="menuitem"
        >
          <span className={css.examActionMenuIcon}>{action.icon}</span>
          <span>{action.label}</span>
        </button>
      ))}

      {releasedAt && (
        <div className={css.examActionMenuStatus}>
          <span aria-hidden="true">✓</span>
          <span>Results released</span>
        </div>
      )}

      {categories.length > 0 && (
        <div className={css.examActionMenuSection}>
          <span className={css.examActionMenuSectionLabel}>Assessment category</span>
          <div className={css.examActionMenuCategoryGrid}>
            {categories.map(category => (
              <button
                key={category.key}
                type="button"
                className={`${css.examActionMenuCategory} ${category.active ? css.examActionMenuCategoryActive : ''}`}
                onClick={() => { setOpen(false); category.onClick(); }}
                disabled={category.disabled}
                aria-pressed={category.active}
                title={category.title}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className={css.examActionMenuAnchor}>
      <button
        ref={buttonRef}
        type="button"
        className={css.examActionTrigger}
        onClick={openMenu}
        aria-label="Open exam actions"
        aria-haspopup="menu"
        aria-expanded={open}
        title="More exam actions"
      >
        <MoreVertIcon fontSize="small" />
      </button>
      </div>
      {menu}
    </>
  );
}